import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import UploadImage from "../common/UploadImage";
import Dialog from "../common/Dialog";

/**
 * Programme management.
 *
 * The previous version of this file was scaffolding: placeholder "Item 1..4"
 * menu entries, empty submit handlers and no API calls at all. This wires the
 * screen to the real /program endpoints.
 */

const asList = (payload, ...keys) => {
  if (Array.isArray(payload)) return payload;
  for (const k of keys) if (Array.isArray(payload?.[k])) return payload[k];
  return Array.isArray(payload?.data) ? payload.data : [];
};

const money = (n) => `$${Number(n || 0).toLocaleString()}`;
const PAGE_SIZE = 8;

const EMPTY = { name: "", description: "", category: "", talent: "", price: "", image: null };

export default function ProgramPage() {
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [talents, setTalents] = useState([]);
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [page, setPage] = useState(1);

  const [editing, setEditing] = useState(null); // null | "new" | program
  const [form, setForm] = useState(EMPTY);
  const [formError, setFormError] = useState("");
  const [saving, setSaving] = useState(false);

  const [pendingDelete, setPendingDelete] = useState(null);
  const [banner, setBanner] = useState(null);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const [p, c, u] = await Promise.all([
        api.get("/program/getAllprograms"),
        api.get("/category/allcategories").catch(() => ({ data: [] })),
        api.get("/users/users").catch(() => ({ data: [] })),
      ]);
      setPrograms(asList(p.data, "programs"));
      setCategories(asList(c.data, "categories"));
      setTalents(asList(u.data, "users"));
      setStatus("ready");
    } catch (err) {
      setLoadError(err.response?.data?.message || "We couldn't load the programmes.");
      setStatus("error");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const categoryName = useMemo(() => {
    const map = new Map(categories.map((c) => [String(c._id), c.name]));
    return (v) => map.get(String(v?._id || v)) || "Uncategorised";
  }, [categories]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return programs.filter((p) => {
      const inCat =
        categoryFilter === "all" ||
        String(p.category?._id || p.category) === categoryFilter;
      const hit =
        !term ||
        p.name?.toLowerCase().includes(term) ||
        p.description?.toLowerCase().includes(term);
      return inCat && hit;
    });
  }, [programs, search, categoryFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  function openCreate() {
    setForm({ ...EMPTY, category: categories[0]?._id || "", talent: talents[0]?._id || "" });
    setFormError("");
    setEditing("new");
  }

  function openEdit(program) {
    setForm({
      name: program.name || "",
      description: program.description || "",
      category: String(program.category?._id || program.category || ""),
      talent: String(program.talent?._id || program.talent || ""),
      price: String(program.price ?? ""),
      image: null,
    });
    setFormError("");
    setEditing(program);
  }

  async function handleSave(e) {
    e.preventDefault();
    setFormError("");

    if (!form.name.trim()) return setFormError("Give the programme a name.");
    if (!form.description.trim()) return setFormError("Add a description.");
    if (!form.category) return setFormError("Choose a category.");
    if (!form.talent) return setFormError("Choose a lead practitioner.");
    if (form.price === "" || Number(form.price) < 0)
      return setFormError("Enter a price of zero or more.");

    // The endpoints run through multer, so the payload must be multipart even
    // when no new file is attached.
    const body = new FormData();
    body.append("name", form.name.trim());
    body.append("description", form.description.trim());
    body.append("category", form.category);
    body.append("talent", form.talent);
    body.append("price", form.price);
    if (form.image) body.append("image", form.image);

    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/program/Addprogram", body);
        setBanner({ kind: "success", text: `"${form.name.trim()}" created.` });
      } else {
        await api.put(`/program/updateprogrambyid/${editing._id}`, body);
        setBanner({ kind: "success", text: `"${form.name.trim()}" updated.` });
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Could not save the programme."
      );
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await api.delete(`/program/deleteprogrambyid/${target._id}`);
      setBanner({ kind: "success", text: `"${target.name}" deleted.` });
      await load();
    } catch (err) {
      setBanner({
        kind: "error",
        text: err.response?.data?.message || "Could not delete that programme.",
      });
    }
  }

  return (
    <div className="es-rise">
      <div className="es-section__head" style={{ marginBottom: "1.5rem" }}>
        <div>
          <h2 className="es-h2" style={{ marginBottom: "0.35rem" }}>Programmes</h2>
          <p className="es-body" style={{ fontSize: "0.93rem", margin: 0 }}>
            Everything families can register for. Changes appear on the public
            site immediately.
          </p>
        </div>
        <button type="button" className="es-btn es-btn--primary" onClick={openCreate}>
          New programme
        </button>
      </div>

      {banner && (
        <div
          className={`es-alert es-alert--${banner.kind === "success" ? "success" : "error"}`}
          role={banner.kind === "success" ? "status" : "alert"}
        >
          {banner.text}
        </div>
      )}

      <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "1.5rem" }}>
        <div className="es-field" style={{ margin: 0, flex: "1 1 15rem" }}>
          <label className="es-label" htmlFor="pg-search">Search programmes</label>
          <input id="pg-search" type="search" className="es-input" placeholder="Name or description"
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>

        <div className="es-field" style={{ margin: 0, flex: "0 1 14rem" }}>
          <label className="es-label" htmlFor="pg-cat">Category</label>
          <select id="pg-cat" className="es-select" value={categoryFilter}
            onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}>
            <option value="all">All categories</option>
            {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
          </select>
        </div>

        <button type="button" className="es-btn es-btn--ghost" onClick={load}>Refresh</button>
      </div>

      {status === "loading" && (
        <div className="es-card" aria-busy="true">
          <div className="es-card__body">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="es-skeleton es-skeleton--text" style={{ width: `${92 - i * 9}%` }} />
            ))}
          </div>
          <span className="es-visually-hidden">Loading programmes</span>
        </div>
      )}

      {status === "error" && (
        <div className="es-alert es-alert--error" role="alert">
          {loadError}{" "}
          <button type="button" className="es-btn es-btn--ghost es-btn--sm" onClick={load}>Retry</button>
        </div>
      )}

      {status === "ready" && filtered.length === 0 && (
        <div className="es-state">
          <p className="es-state__title">
            {programs.length === 0 ? "No programmes yet" : "Nothing matches those filters"}
          </p>
          <p>
            {programs.length === 0
              ? "Create the first programme to get started."
              : "Try a different search term or clear the category filter."}
          </p>
        </div>
      )}

      {status === "ready" && filtered.length > 0 && (
        <>
          <div className="es-table-wrap">
            <table className="es-table es-table--numeric">
              <caption className="es-visually-hidden">Programmes</caption>
              <thead>
                <tr>
                  <th scope="col">Programme</th>
                  <th scope="col">Category</th>
                  <th scope="col">Price</th>
                  <th scope="col">Enrolled</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((p) => (
                  <tr key={p._id}>
                    <td>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", minWidth: "14rem" }}>
                        <UploadImage file={p.image} alt=""
                          style={{ width: "3rem", height: "3rem", borderRadius: "var(--es-r-md)", objectFit: "cover", flex: "none" }} />
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: "var(--es-ink)" }}>{p.name}</div>
                          <div className="es-muted" style={{
                            maxWidth: "22rem", overflow: "hidden",
                            textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {p.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td><span className="es-badge es-badge--neutral">{categoryName(p.category)}</span></td>
                    <td>{money(p.price)}</td>
                    <td>{p.participantsCount || 0}</td>
                    <td>
                      <div style={{ display: "flex", gap: "0.4rem" }}>
                        <button type="button" className="es-btn es-btn--ghost es-btn--sm"
                          aria-label={`Edit ${p.name}`} onClick={() => openEdit(p)}>
                          Edit
                        </button>
                        <button type="button" className="es-btn es-btn--danger es-btn--sm"
                          aria-label={`Delete ${p.name}`} onClick={() => setPendingDelete(p)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "1rem", flexWrap: "wrap" }}>
            <button type="button" className="es-btn es-btn--ghost es-btn--sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={safePage <= 1}>
              Previous
            </button>
            <span className="es-muted" aria-live="polite">
              Page {safePage} of {totalPages} · {filtered.length} programme{filtered.length === 1 ? "" : "s"}
            </span>
            <button type="button" className="es-btn es-btn--ghost es-btn--sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={safePage >= totalPages}>
              Next
            </button>
          </div>
        </>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={editing === "new" ? "New programme" : `Edit ${editing?.name || ""}`}
        size="lg"
      >
        {formError && <div className="es-alert es-alert--error" role="alert">{formError}</div>}

        <form onSubmit={handleSave}>
          <div className="es-field">
            <label className="es-label" htmlFor="pf-name">Name</label>
            <input id="pf-name" className="es-input" value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>

          <div className="es-field">
            <label className="es-label" htmlFor="pf-desc">Description</label>
            <textarea id="pf-desc" className="es-textarea" rows={4} value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          </div>

          <div className="es-row">
            <div className="es-field">
              <label className="es-label" htmlFor="pf-cat">Category</label>
              <select id="pf-cat" className="es-select" value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                <option value="">Choose a category</option>
                {categories.map((c) => <option key={c._id} value={c._id}>{c.name}</option>)}
              </select>
            </div>

            <div className="es-field">
              <label className="es-label" htmlFor="pf-price">Price (USD)</label>
              <input id="pf-price" className="es-input" type="number" min="0" step="1"
                value={form.price}
                onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))} />
            </div>
          </div>

          <div className="es-field">
            <label className="es-label" htmlFor="pf-talent">Lead practitioner</label>
            <select id="pf-talent" className="es-select" value={form.talent}
              onChange={(e) => setForm((f) => ({ ...f, talent: e.target.value }))}>
              <option value="">Choose a practitioner</option>
              {talents.map((t) => (
                <option key={t._id} value={t._id}>
                  {t.firstname} {t.lastname}
                </option>
              ))}
            </select>
          </div>

          <div className="es-field">
            <label className="es-label" htmlFor="pf-image">
              Cover image {editing !== "new" && <span className="es-muted">(optional — leave empty to keep the current one)</span>}
            </label>
            <input id="pf-image" className="es-input" type="file" accept="image/*"
              onChange={(e) => setForm((f) => ({ ...f, image: e.target.files?.[0] || null }))} />
            <p className="es-hint">JPEG, PNG, WebP or GIF, up to 5MB.</p>
          </div>

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
            <button type="submit" className="es-btn es-btn--primary" disabled={saving}>
              {saving ? "Saving…" : editing === "new" ? "Create programme" : "Save changes"}
            </button>
            <button type="button" className="es-btn es-btn--ghost" onClick={() => setEditing(null)}>
              Cancel
            </button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={pendingDelete !== null}
        onClose={() => setPendingDelete(null)}
        title="Delete this programme?"
        tone="danger"
      >
        <p className="es-body">
          <strong>{pendingDelete?.name}</strong> will be removed permanently.
          {pendingDelete?.participantsCount
            ? ` It currently has ${pendingDelete.participantsCount} enrolment${pendingDelete.participantsCount === 1 ? "" : "s"}.`
            : ""}
          {" "}This cannot be undone.
        </p>
        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1.5rem" }}>
          <button type="button" className="es-btn es-btn--danger" onClick={confirmDelete}>
            Delete programme
          </button>
          <button type="button" className="es-btn es-btn--ghost" onClick={() => setPendingDelete(null)}>
            Cancel
          </button>
        </div>
      </Dialog>
    </div>
  );
}
